import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../axiosConfig';
import { completeImageUrl } from '../utils/imageUrl';
import { itemsWord } from '../utils/pluralize';
import { shopInitials } from '../utils/shopInitials';
import './SearchPage.css';

// Full search results: shops first, then the products as a list of cards (same family as the cart list).
export default function SearchPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const query = (params.get('q') || '').trim();
  const [results, setResults] = useState({ products: [], shops: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query.length < 2) {
      setResults({ products: [], shops: [] });
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    axios
      .get('/api/v1/search', { params: { q: query, limit: 50 } })
      .then((response) => {
        if (!active) return;
        setResults({
          products: response.data?.products || [],
          shops: response.data?.shops || [],
        });
      })
      .catch((error) => console.error('Search error:', error))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [query]);

  const openShop = (shop) => {
    const handle = shop.name ? (shop.name.startsWith('@') ? shop.name : `@${shop.name}`) : null;
    navigate(handle ? `/${handle}` : `/account/${shop.id}`);
  };

  const { products, shops } = results;
  const isEmpty = !loading && products.length === 0 && shops.length === 0;

  if (loading) {
    return (
      <div className="search-page">
        <div className="search-page__loading"><div className="search-page__loader" /></div>
      </div>
    );
  }

  return (
    <div className="search-page">
      <div className="search-page__head">
        <h1 className="search-page__title">Результати пошуку</h1>
        <p className="search-page__query">«{query}»</p>
      </div>

      {isEmpty && (
        <div className="search-page__empty">
          <p>Нічого не знайдено</p>
          <button type="button" className="search-page__empty-btn" onClick={() => navigate('/')}>До товарів</button>
        </div>
      )}

      {shops.length > 0 && (
        <section className="search-page__section">
          <h2 className="search-page__label">Магазини</h2>
          {shops.map((shop) => (
            <button key={`shop-${shop.id}`} type="button" className="search-page__card search-page__shop" onClick={() => openShop(shop)}>
              <span
                className="search-page__avatar"
                style={shop.image ? { backgroundImage: `url(${completeImageUrl(shop.image)})` } : undefined}
              >
                {!shop.image && shopInitials(shop.title || shop.name)}
              </span>
              <span className="search-page__shop-text">
                <span className="search-page__name">{shop.title || shop.name}</span>
                <span className="search-page__sub">{shop.name}</span>
              </span>
              <span className="search-page__chevron" aria-hidden="true">›</span>
            </button>
          ))}
        </section>
      )}

      {products.length > 0 && (
        <section className="search-page__section">
          <h2 className="search-page__label">Товари · {products.length} {itemsWord(products.length)}</h2>
          {products.map((product) => (
            <button
              key={`product-${product.id}`}
              type="button"
              className="search-page__card search-page__product"
              onClick={() => navigate(`/products/${product.id}?from=search`)}
            >
              <span
                className="search-page__image"
                style={product.image ? { backgroundImage: `url(${completeImageUrl(product.image)})` } : undefined}
              />
              <span className="search-page__product-main">
                <span className="search-page__name search-page__name--clamp">{product.title}</span>
                <span className="search-page__meta">
                  {product.category && <span className="search-page__chip">{product.category}</span>}
                </span>
                {product.price ? <span className="search-page__price">від {product.price} ₴</span> : null}
              </span>
            </button>
          ))}
        </section>
      )}
    </div>
  );
}
